import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Alert, Button, Card, CardBody, CardFooter, Col, Row,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import CancelNominatedTradeMutation from 'src/mutations/CancelNominatedTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';
import {
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
  TRADE_RULE_CANCEL, TRADE_RULE_STATE_PROPOSED,
} from 'src/util/constants';

import ProposedTradeRule from './ProposedTradeRule';

const ProposedTradeRulesFragment = graphql`
  fragment ProposedTradeRules_property on Property {
    id
    meters {
      edges {
        node {
          id
          identifier
          title
          tradePointId
          proposedRules: rules(first: 500, state: TRADE_RULE_STATE_PROPOSED) {
            edges {
              node {
                id
                priority
                tradeType
                state
                buyer {
                  userId
                  user { id email givenName familyName }
                  communityId
                  residualId
                  tradePoint { id type }
                }
                seller {
                  userId
                  user { id email givenName familyName }
                  communityId
                  residualId
                  tradePoint { id type }
                }
                clauses {
                  edges {
                    node {
                      price
                      id
                      timezone
                      ignoreDaylightSavings
                      ignorePublicHolidays
                      monthsOfYear
                      daysOfWeek
                      timesOfDay {
                        start  { hours minutes seconds }
                        finish { hours minutes seconds }
                      }
                    }
                  }
                }
                start
                finish
                proposedAt
                proposedBy { id email givenName familyName }
              }
            }
          }
        }
      }
    }
  }
`;

const mutationName = 'cancelNominatedTrade';

/**
 * Extract trade rules for a property, categorizing them by buy/sell direction
 * for a trade rule state proposed.
 * @param {object} property - Property object containing meters with proposed rules
 * @returns {object} Object with sell, buy arrays and count of total rules
 */
function getTradeRulesProposedForProperty(property) {
  const rules = { sell: [], buy: [], count: 0 };

  if (!property?.meters?.edges) {
    return rules;
  }

  const { meters } = property;

  meters.edges.forEach((meterEdge) => {
    meterEdge?.node?.proposedRules?.edges?.forEach((ruleEdge) => {
      const { node: ruleNode } = ruleEdge;
      if (ruleNode.state !== TRADE_RULE_STATE_PROPOSED) return;

      if (ruleNode.seller.tradePoint.id === meterEdge.node.tradePointId) {
        rules.sell.push({ ...ruleNode, meter: meterEdge.node });
      } else {
        rules.buy.push({ ...ruleNode, meter: meterEdge.node });
      }
    });
  });
  rules.count = rules.buy.length + rules.sell.length;

  return rules;
}

/**
 * Handles failure responses from nominated trade rule mutations by displaying error
 * messages to the user.
 * @param {Error|string} error - Error object or message from the failed mutation.
 */
const handleNominatedTradeMutationFailure = (error) => {
  FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
};

/**
 * Handles successful responses from closing nominated trade rule mutations by displaying success
 * messages.
 * @param {import('react-intl').IntlShape} intl - the intl object
 * @param {object} response - GraphQL mutation response object
 * @param {object} response.cancelNominatedTrade - Nested response containing cancelled trade
 * rule data
 * @param {string} response.cancelNominatedTrade.id - id of the successfully cancelled trade
 *  rule
 */
const handleCancelNominatedTradeMutationSuccess = (intl, response) => {
  const { id } = response.cancelNominatedTrade;
  const successMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.response_message.success', defaultMessage: 'Cancelled peer-to-peer trade rule proposal with id: {id}.' }, { id });

  FlashesStore.flash(
    FlashesStore.SUCCESS,
    successMsg,
    mutationName,
  );
};

/**
 * Executes trade rule actions based on the specified action type for nominated trade rules.
 *
 * This function serves as a dispatcher for different trade rule operations, currently supporting
 * the CANCEL action for nominated trade rules. It handles the business logic for determining
 * which mutation to execute and provides appropriate error handling for unsupported actions.
 * @param {import('react-intl').IntlShape} intl - the intl object
 * @param {object} rule - TradeRule object from syndesi.graphql schema
 * @param {string} rule.id - ID of the trade rule
 * @param {string} action - Action constant defining the operation to perform
 * @returns {void|Promise} Returns a Promise if the action is supported and executed, otherwise
 */
const tradeRuleAction = (intl, rule, action) => {
  const { id } = rule;
  const errorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.response_message.error', defaultMessage: 'Action {action} is not possible.' }, { action });

  if (action === TRADE_RULE_CANCEL) {
    return CancelNominatedTradeMutation(
      { id },
      (response) => handleCancelNominatedTradeMutationSuccess(intl, response),
      handleNominatedTradeMutationFailure,
      null, // formatMessage parameter
    );
  }

  return FlashesStore.flash(
    FlashesStore.ERROR,
    errorMsg,
    mutationName,
  );
};

export const tradeRuleActionButton = (intl, rule) => {
  const {
    state, meter, buyer, seller,
  } = rule || {};

  if (state !== TRADE_RULE_STATE_PROPOSED) {
    return null;
  }
  const cancelProposedTradeLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.cancel.label', defaultMessage: 'Cancel proposed trade' });

  if (
    [buyer.tradePoint.id, seller.tradePoint.id].includes(meter.tradePointId)
  ) {
    return (
      <Button onClick={() => tradeRuleAction(intl, rule, TRADE_RULE_CANCEL)}>
        {cancelProposedTradeLabel}
      </Button>
    );
  }

  return (
    <Alert color="danger">
      {intl.formatMessage(
        { id: 'trade_rule.trade_rule_active.proposed_trade_rules.unknown_trade_point.alert', defaultMessage: 'Unknown trade point: {meterId} when buyer is {buyerTradePointId} and seller is {sellerTradePointId}' },
        {
          meterId: meter.id,
          buyerTradePointId: buyer.tradePoint.id,
          sellerTradePointId: seller.tradePoint.id,
        },
      )}
    </Alert>
  );
};

/**
 * ProposedTradeRules component displays proposed trade rules for a property.
 * @param {object} props - Component props
 * @param {object} props.property - Property containing meters and their associated proposed rules
 * @returns {React.ReactElement} A Card component containing proposed trade information.
 */
function ProposedTradeRules(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  const intl = useIntl();

  let { property } = props;
  if (!property) {
    return <Loading />;
  }
  property = useFragment(ProposedTradeRulesFragment, property);

  // Internationalization messages
  const proposedTradesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.proposed_trades.label', defaultMessage: 'Proposed Trades' });
  const buyingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.buying.title', defaultMessage: 'Buying' });
  const viewAllTradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.view_all_trade_rules.label', defaultMessage: 'View all trade rules' });
  const proposeNewTradeLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.propose_new_trade.label', defaultMessage: 'Propose new trade' });

  const tradeRules = getTradeRulesProposedForProperty(property);

  if (tradeRules.count === 0) {
    return null;
  }

  const singleProposal = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.proposal.singular', defaultMessage: 'proposed trade rule' });
  const multipleProposal = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.proposal.multiple', defaultMessage: 'proposed trade rules' });
  const buyProposalCount = tradeRules.buy.length;
  const sellProposalCount = tradeRules.sell.length;
  const sellingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.selling.title', defaultMessage: 'Selling' });

  return (
    <Card className="mt-4 mb-4 trade-rules-proposed">
      <CardBody>
        <h2>{proposedTradesTitle}</h2>
        <p>
          <FormattedMessage
            id="trade_rule.trade_rule_active.proposed_trade_rules.awaiting.response.help_text"
            defaultMessage="We are waiting on your friends to get back in touch."
            values={{
              link: (chunks) => (
                <LinkInI18nBlock
                  url={intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rules.awaiting.response.help_text.url', defaultMessage: 'trade_rule.trade_rule_active.proposed_trade_rules.awaiting.response.help_text.url' })}
                  urlKey="trade_rule.trade_rule_active.proposed_trade_rules.awaiting.response.help_text.url"
                  chunks={chunks}
                />
              ),
            }}
          />
        </p>
        <Row>
          <Col xs="12" sm="12" md="6">
            <h3>{buyingTitle}</h3>
            {intl.formatMessage(
              {
                id: 'trade_rule.trade_rule_active.proposed_trade_rules.buy.count_details',
                defaultMessage: 'You have {proposalCount} {proposalCountLabel} to buy energy',
              },
              {
                proposalCount: <strong>{buyProposalCount}</strong>,
                proposalCountLabel: <FormattedPlural
                  value={buyProposalCount}
                  one={singleProposal}
                  other={multipleProposal}
                />,
              },
            )}

            {tradeRules?.buy?.map((rule) => (
              <ProposedTradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_BUY}
                propertyId={property.id}
                actionButtonFn={tradeRuleActionButton}
                key={rule.id}
              />
            ))}
          </Col>
          <Col xs="12" sm="12" md="6">
            <h3>{sellingTitle}</h3>
            {intl.formatMessage(
              {
                id: 'trade_rule.trade_rule_active.proposed_trade_rules.sell.count_details',
                defaultMessage: 'You have {proposalCount} {proposalCountLabel} to sell energy',
              },
              {
                proposalCount: <strong>{sellProposalCount}</strong>,
                proposalCountLabel: <FormattedPlural
                  value={sellProposalCount}
                  one={singleProposal}
                  other={multipleProposal}
                />,

              },
            )}

            {tradeRules?.sell?.map((rule) => (
              <ProposedTradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_SELL}
                propertyId={property.id}
                actionButtonFn={tradeRuleActionButton}
                key={rule.id}
              />
            ))}
          </Col>
        </Row>
      </CardBody>
      <CardFooter>
        <Link
          to={`/properties/${property.id}/trade-rules?type=TRADE_RULE_TYPE_PROPOSED`}
        >
          <Button color="primary" className="me-2">
            {viewAllTradeRulesLabel}
          </Button>
        </Link>
        <Link
          to={`/properties/${property.id}/trade-rules/propose/nominated`}
        >
          <Button color="primary" className="me-2">
            {proposeNewTradeLabel}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}

ProposedTradeRules.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

ProposedTradeRules.defaultProps = {
  property: null,
};

export { getTradeRulesProposedForProperty };
export default ProposedTradeRules;
